import { EmailRecordProps } from '@/models/Email';
import { Box, Button, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { adminDataGridSx } from '@/components/admin/adminDataGridStyles';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useEmailRecord from '@/hooks/useEmailRecord';
import { GridLocaleTextOverride } from '@/utils/dataGridUtil';

const columns = (
  formatDate: (value: Date | string | null | undefined) => string,
  formatTime: (value: Date | string | null | undefined) => string,
  formatDateTime: (value: Date | string | null | undefined) => string,
  toUtcTimestamp: (value: Date | string | null | undefined) => number
): GridColDef<EmailRecordProps>[] => [
  { field: 'id', headerName: 'Email ID', flex: 0.5 },
  {
    field: 'sentDate',
    headerName: 'Sent Date',
    flex: 1,
    valueGetter: (_value, row) => row.sentAtUtc,
    valueFormatter: (value: Date | string | null | undefined) => formatDate(value),
    sortComparator: (v1: Date | string | null | undefined, v2: Date | string | null | undefined) =>
      toUtcTimestamp(v1) - toUtcTimestamp(v2),
  },
  {
    field: 'sentTime',
    headerName: 'Sent Time',
    flex: 1,
    valueGetter: (_value, row) => row.sentAtUtc,
    valueFormatter: (value: Date | string | null | undefined) => formatTime(value),
    sortComparator: (v1: Date | string | null | undefined, v2: Date | string | null | undefined) =>
      toUtcTimestamp(v1) - toUtcTimestamp(v2),
  },
  { field: 'sentToEmail', headerName: 'Email', flex: 1 },
  { field: 'emailStatus', headerName: 'Status', flex: 1 },
  { field: 'emailType', headerName: 'Email Type', flex: 1 },
  { field: 'lastErrorMessage', headerName: 'Last Error Message', flex: 2 },
];

const EmailActivity: React.FC = () => {
  const { locale } = useContext(LocaleContext);
  const { fetchEmailActivity } = useEmailRecord();

  const [rows, setRows] = useState<EmailRecordProps[]>([]);
  const [loading, setLoading] = useState(true);

  const timeZoneLabel = useMemo(() => {
    const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shortTimeZone =
      Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value ?? null;

    return shortTimeZone != null && shortTimeZone !== ''
      ? `${resolvedTimeZone} (${shortTimeZone})`
      : resolvedTimeZone;
  }, []);

  const parseUtcDate = useCallback((value: Date | string | null | undefined): Date | null => {
    if (value == null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const normalizedValue =
      /Z$|[+-]\d{2}:?\d{2}$/.test(value) || !value.includes('T') ? value : `${value}Z`;
    const parsedDate = new Date(normalizedValue);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, []);

  const toUtcTimestamp = useCallback(
    (value: Date | string | null | undefined): number => {
      const parsedDate = parseUtcDate(value);
      return parsedDate == null ? Number.NEGATIVE_INFINITY : parsedDate.getTime();
    },
    [parseUtcDate]
  );

  const formatDateTime = useCallback(
    (value: Date | string | null | undefined): string => {
      const parsedDate = parseUtcDate(value);

      if (parsedDate == null) {
        return '';
      }

      return parsedDate.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    [parseUtcDate]
  );

  const formatDate = useCallback(
    (value: Date | string | null | undefined): string => {
      const parsedDate = parseUtcDate(value);

      if (parsedDate == null) {
        return '';
      }

      return parsedDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    [parseUtcDate]
  );

  const formatTime = useCallback(
    (value: Date | string | null | undefined): string => {
      const parsedDate = parseUtcDate(value);

      if (parsedDate == null) {
        return '';
      }

      return parsedDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    [parseUtcDate]
  );

  const loadEmailActivity = useCallback(async () => {
    setLoading(true);

    try {
      const data = await fetchEmailActivity();
      setRows(data ?? []);
    } finally {
      setLoading(false);
    }
  }, [fetchEmailActivity]);

  const EmailActivityToolbar = useCallback(
    (props: GridToolbarContainerProps) => (
      <GridToolbarContainer {...props}>
        <GridToolbarFilterButton />
        <Button onClick={() => void loadEmailActivity()} disabled={loading} size='small'>
          Refresh Data
        </Button>
        <div style={{ flex: 1 }} />
        <Typography variant='subtitle3' sx={{ mr: 2 }}>
          {`Times shown in ${timeZoneLabel}`}
        </Typography>
        <GridToolbarQuickFilter debounceMs={1500} />
      </GridToolbarContainer>
    ),
    [loadEmailActivity, loading, timeZoneLabel]
  );

  useEffect(() => {
    void loadEmailActivity();
  }, [loadEmailActivity]);

  return (
    <Box sx={{ height: '65vh', width: '100%' }}>
      <DataGridPremium
        showToolbar
        loading={loading}
        localeText={GridLocaleTextOverride(locale)}
        columns={columns(formatDate, formatTime, formatDateTime, toUtcTimestamp)}
        rows={rows}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        density='compact'
        initialState={{
          sorting: {
            sortModel: [{ field: 'sentDate', sort: 'desc' }],
          },
        }}
        pagination
        slots={{ toolbar: EmailActivityToolbar }}
        sx={adminDataGridSx}
      />
    </Box>
  );
};

export default EmailActivity;
